import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { LocalizationsBlock, validateLocalizations } from '../LocalizationsBlock';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';
import { mapLocalizationsFromApi } from '../../shared/utils/buildEntityFormData.js';

const LOCALIZATION_FIELDS = [{ name: 'title', label: 'Название', type: 'text', required: true }];

const getErrorMessage = (error, fallback) =>
  error?.error || error?.response?.data?.error || error?.message || fallback;

/**
 * Форма справочника «название + переводы» (категории, индустрии, форматы…).
 * Создание и редактирование — одним JSON-запросом вместе с localizations.
 *
 * @param {Function} load — (id) => Promise<сущность с localizations>
 * @param {Function} create — (body) => Promise
 * @param {Function} update — (body) => Promise
 * @param {string} backTo — путь списка
 * @param {string} [placeholder] — подсказка в поле названия
 * @param {string} [createdText] / [savedText]
 */
export const TitleEntityForm = ({
  load,
  create,
  update,
  backTo,
  placeholder = '',
  createdText = 'Создано',
  savedText = 'Сохранено',
}) => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(isEdit ? null : { title: '' });
  const [localizations, setLocalizations] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    load(id)
      .then((res) => {
        setForm({ id: res.id, title: res.title || '' });
        setLocalizations(mapLocalizationsFromApi(res.localizations, []));
      })
      .catch((error) => toast.error(getErrorMessage(error, 'Не удалось загрузить данные')));
  }, [id]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.title.trim()) {
      toast.error('Введите название');
      return;
    }
    const localizationsError = validateLocalizations(localizations, LOCALIZATION_FIELDS);
    if (localizationsError) {
      toast.error(localizationsError);
      return;
    }

    setSaving(true);
    try {
      const body = {
        ...form,
        localizations: localizations.map(({ language, title, id: localizationId }) => ({ id: localizationId, language, title })),
      };
      if (isEdit) {
        await update(body);
        toast.success(savedText);
      } else {
        await create(body);
        toast.success(createdText);
        navigate(backTo);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <FormLoading />;

  return (
    <FormLayout onSubmit={handleSubmit} onBack={() => navigate(backTo)} saving={saving} submitText={isEdit ? 'Сохранить' : 'Создать'}>
      <FormSection title="Основная информация">
        <Row>
          <Col md={6}>
            <FormField label="Название" required>
              <Form.Control
                type="text"
                value={form.title}
                placeholder={placeholder}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <LocalizationsBlock value={localizations} onChange={setLocalizations} fields={LOCALIZATION_FIELDS} source={form} aiContext="название категории или справочника сайта" />
    </FormLayout>
  );
};
